import { UrlObject } from "url";
import { pagesPath } from "faqSrc/lib/$path";
import Link from "next/link";
import React, { FC } from "react";
import styled from "styled-components";
import IconHome from "~/components/icons/IconHome";
import BreadcrumbStructuredData from "~/components/modules/structuredData/breadcrumb";
import {
  convertUrlObjectToString,
  externalLinkAttrs,
} from "~/lib/utils/urlHelper";

export interface BreadcrumbItemData {
  locale?: string;
  text: string;
  url?: UrlObject;
}

interface Props {
  homeUrl?: string;
  items: BreadcrumbItemData[];
}

const SingleLineBreadcrumb: FC<Props> = ({ homeUrl, items }) => {
  if (items?.length < 1) {
    return <></>;
  }

  return (
    <>
      <BreadcrumbWrap className="content-wrap hide-scrollbar">
        <Link
          href={homeUrl ?? convertUrlObjectToString({ url: pagesPath.$url() })}
          passHref
        >
          <ItemLink
            aria-label="Home page"
            {...(homeUrl ? externalLinkAttrs(homeUrl) : {})}
          >
            <IconHome />
          </ItemLink>
        </Link>

        {items.map((item, idx) => {
          if (item.url) {
            return (
              <BreadcrumbItem key={idx}>
                <BreadcrumbItemSeparator>/</BreadcrumbItemSeparator>
                <Link
                  href={convertUrlObjectToString({
                    locale: item.locale,
                    url: item.url,
                  })}
                  passHref
                >
                  <ItemLink {...externalLinkAttrs(item.url)}>
                    {item.text}
                  </ItemLink>
                </Link>
              </BreadcrumbItem>
            );
          }

          return (
            <BreadcrumbItem key={idx}>
              <BreadcrumbItemSeparator>/</BreadcrumbItemSeparator>
              <Item key={idx}>{item.text}</Item>
            </BreadcrumbItem>
          );
        })}
      </BreadcrumbWrap>
      {items?.length >= 2 && <BreadcrumbStructuredData items={items} />}
    </>
  );
};

const BreadcrumbWrap = styled.div`
  align-items: center;
  border-top: 2px solid var(--sep-bdr-clr);
  color: var(--cap-clr);
  display: grid;
  font-size: 16px;
  gap: 8px;
  grid-auto-flow: column;
  justify-content: start;
  line-height: 30px;
  overflow-x: auto;
  padding: 12px 16px;
  white-space: nowrap;
`;

const BreadcrumbItem = styled.div`
  display: grid;
  grid-auto-flow: column;
`;

const ItemLink = styled.a`
  color: var(--cap-clr);
  display: grid;
  font-weight: 400;
  text-decoration: underline;

  &:hover,
  &:active {
    text-decoration: none;
    color: var(--sub-txt-clr);
  }
`;

const Item = styled.span`
  font-weight: 700;
`;

const BreadcrumbItemSeparator = styled.span`
  font-weight: 400;
  margin-right: 8px;
`;

export default SingleLineBreadcrumb;
